import { useEffect, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useParams, Link, useNavigate } from "react-router-dom"
import {
  Calendar, Clock, Users, Video, Play, Download,
  MessageCircle, BarChart2, FileText, ChevronDown, ChevronUp,
  Search, Mic, Camera, CameraOff, MicOff, Copy
} from "lucide-react"
import noTranscriptSvg from "@/assets/noTranscript.svg"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { Button } from "@/clientWeb/components/ui/button"
import { Badge } from "@/clientWeb/components/ui/badge"
import { FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import { meetingHistoryService, type PastMeetingDetail } from "./services/meeting-history.service"
import { downloadPlayback, openPlayback } from "@/features/meeting/services/recording-link"
import { formatMeetingTime } from "./services/meetings.service"
import { useMeetingsStore, selectMeetingById } from "./store/meetings.store"
import { displayedMeetingTimes } from "./utils/actual-duration"
import { AttachmentLink } from "@/features/messenger/private-attachment-ui"

function Avatar({ name, imageUrl, size = 36 }: { name: string; imageUrl?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const ci = name.charCodeAt(0) % colors.length
  return (
    <div
      className={`rounded-full flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden ${colors[ci]}`}
      style={{ width: size, height: size }}
    >
      {imageUrl ? <img src={imageUrl} alt={name} className="w-full h-full object-cover" /> : initials}
    </div>
  )
}

type TabId = "overview" | "participants" | "recordings" | "chat" | "transcription"

export default function PastMeetingPage() {
  const t = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>("overview")
  const [chatSearch, setChatSearch] = useState("")
  const [transcriptSearch, setTranscriptSearch] = useState("")
  const [showAllParticipants, setShowAllParticipants] = useState(false)
  // The meeting's own record — title and times — is the one the meetings list
  // already loaded; recordings and chat come from their APIs below.
  const meeting = useMeetingsStore(selectMeetingById(id))
  const meetingsLoading = useMeetingsStore((s) => s.loading)
  const meetingUuid = meeting?.id
  const [m, setM] = useState<PastMeetingDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!meetingUuid) return
    let cancelled = false
    setLoading(true)
    meetingHistoryService.getPastMeeting(meetingUuid)
      .then((data) => { if (!cancelled) setM(data) })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Failed to load meeting', description: err instanceof Error ? err.message : 'Please try again' })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [meetingUuid])

  if (!meeting) {
    if (meetingsLoading) return <FullPageDotsLoader />
    return (
      <div className="flex flex-col flex-1 items-center justify-center p-8 text-center">
        <h2 className="text-base font-semibold text-foreground mb-1">{t("meetings.notFound")}</h2>
        <p className="text-sm text-muted-foreground mb-4">{t("meetings.notFoundDesc")}</p>
        <Button onClick={() => navigate("/meetings")}>{t("meetings.backToMeetings")}</Button>
      </div>
    )
  }
  if (loading || !m) return <FullPageDotsLoader />

  const shown = displayedMeetingTimes(meeting)
  const formatDate = (ms: number) => new Date(ms).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })

  const filteredChat = chatSearch.trim()
    ? m.chatMessages.filter(msg => msg.text.toLowerCase().includes(chatSearch.toLowerCase()) || msg.sender.toLowerCase().includes(chatSearch.toLowerCase()))
    : m.chatMessages

  const filteredTranscript = transcriptSearch.trim()
    ? m.transcript.filter(e =>
        e.text.toLowerCase().includes(transcriptSearch.toLowerCase()) ||
        e.speaker.toLowerCase().includes(transcriptSearch.toLowerCase())
      )
    : m.transcript

  const TABS: { id: TabId; label: string; Icon: React.ElementType }[] = [
    { id: "overview",      label: t("meetings.overview"),      Icon: BarChart2 },
    { id: "participants",  label: t("common.participants"),    Icon: Users },
    { id: "recordings",   label: t("meetings.recordings"),    Icon: Video },
    { id: "chat",         label: t("presence.chats"),         Icon: MessageCircle },
    { id: "transcription",label: t("meetings.transcription"), Icon: FileText },
  ]

  const visibleParticipants = showAllParticipants ? m.participants : m.participants.slice(0, 4)

  const noAttendance = (
    <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
      <Users className="w-10 h-10 text-muted-foreground/30" />
      <p className="text-sm text-muted-foreground">{t("meetings.attendanceNotRecorded")}</p>
    </div>
  )

  const exportParticipantsCSV = () => {
    const header = "Name,Email,Join Time,Leave Time,Duration (min),Host,Audio,Video"
    const rows = m.participants.map(p =>
      `${p.name},${p.email ?? ""},${p.joinTime},${p.leaveTime},${p.durationMin},${p.isHost ? "Yes" : "No"},${p.audioOn ? "On" : "Off"},${p.videoOn ? "On" : "Off"}`
    )
    const csv = [header, ...rows].join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a"); a.href = url; a.download = `${meeting.title.replace(/\s+/g, "_")}_participants.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Top bar */}
      <div className="px-4 py-3 sm:px-6 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <Link to="/meetings" className="hover:text-foreground transition-colors">{t("meetings.title")}</Link>
          <span>/</span>
          <Link to={`/meetings/${id}`} className="hover:text-foreground transition-colors">{meeting.title}</Link>
          <span>/</span>
          <span className="text-foreground">{t("meetings.pastMeeting")}</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-foreground">{meeting.title}</h1>
              <Button
                variant="outline" size="sm"
                className="gap-1.5 text-xs"
                onClick={() => navigate(`/meetings/${id}/attendance`)}
              >
                <Users className="w-3.5 h-3.5" />{t("meetings.attendance")}
              </Button>
              <Button
                variant="outline" size="sm"
                className="gap-1.5 text-xs"
                onClick={() => navigate(`/meetings/${id}/attendance-reports`)}
              >
                <BarChart2 className="w-3.5 h-3.5" />{t("meetings.reports")}
              </Button>
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(shown.startMs)}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="w-3.5 h-3.5" />
                {formatMeetingTime(shown.startMs)} – {formatMeetingTime(shown.endMs)}
              </span>
            </div>
          </div>
          <Badge variant="secondary" className="text-xs shrink-0">{shown.minutes} {t("meetings.min")}</Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 px-4 sm:px-6 bg-card border-b border-border shrink-0 overflow-x-auto scrollbar-hide">
        {TABS.map(({ id: tab, label, Icon }) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors shrink-0
              ${activeTab === tab
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">

        {/* ── Overview ── */}
        {activeTab === "overview" && (
          <div className="p-4 sm:p-6 grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: t("meetings.duration"), value: `${shown.minutes} ${t("meetings.min")}`, icon: Clock },
              // Not recorded yet: a count of 0 would claim nobody came.
              { label: t("common.participants"), value: m.participants.length ? m.participants.length.toString() : "—", icon: Users },
              { label: t("meetings.recordings"), value: m.recordings.length.toString(), icon: Video },
              { label: t("meetings.chatMessages"), value: m.chatMessages.length.toString(), icon: MessageCircle },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="bg-card rounded-xl border border-border p-4 flex flex-col gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{label}</span>
                </div>
                <span className="text-2xl font-bold text-foreground">{value}</span>
              </div>
            ))}

            <div className="col-span-2 sm:col-span-4 bg-card rounded-xl border border-border p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t("meetings.participantAttendance")}</h3>
              {m.participants.length === 0 && noAttendance}
              <div className="space-y-2">
                {m.participants.map(p => {
                  const pct = Math.round((p.durationMin / (shown.minutes || 1)) * 100)
                  return (
                    <div key={p.id} className="flex items-center gap-3">
                      <Avatar name={p.name} imageUrl={p.imageUrl} size={28} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-medium text-foreground truncate">{p.name}</span>
                          <span className="text-xs text-muted-foreground ml-2 shrink-0">{p.durationMin} min</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Participants ── */}
        {activeTab === "participants" && (
          <div className="p-4 sm:p-6">
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">{t("common.participants")}</h3>
                {m.participants.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={exportParticipantsCSV}>
                    <Download className="w-3.5 h-3.5" />{t("meetings.exportCsv")}
                  </Button>
                )}
              </div>
              {m.participants.length === 0 && noAttendance}
              <div className="divide-y divide-border/50">
                {visibleParticipants.map(p => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-3">
                    <Avatar name={p.name} imageUrl={p.imageUrl} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{p.name}</span>
                        {p.isHost && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">{t("meetings.host")}</Badge>}
                      </div>
                      <span className="text-xs text-muted-foreground">{p.joinTime} → {p.leaveTime} · {p.durationMin} min</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {p.audioOn ? <Mic className="w-3.5 h-3.5 text-green-500" /> : <MicOff className="w-3.5 h-3.5 text-muted-foreground/40" />}
                      {p.videoOn ? <Camera className="w-3.5 h-3.5 text-green-500" /> : <CameraOff className="w-3.5 h-3.5 text-muted-foreground/40" />}
                    </div>
                  </div>
                ))}
              </div>
              {m.participants.length > 4 && (
                <button
                  onClick={() => setShowAllParticipants(!showAllParticipants)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 text-xs text-primary font-medium border-t border-border hover:bg-muted/30 transition-colors"
                >
                  {showAllParticipants
                    ? <><ChevronUp className="w-3.5 h-3.5" />{t("meetings.showLess")}</>
                    : <><ChevronDown className="w-3.5 h-3.5" />{t("meetings.showLess").replace("less", "")}+{m.participants.length - 4} {t("common.more")}</>}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Recordings ── */}
        {activeTab === "recordings" && (
          <div className="p-4 sm:p-6">
            {m.recordings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                  <Video className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-foreground">{t("meetings.noRecordings")}</p>
                <p className="text-xs text-muted-foreground">{t("meetings.noRecordingsDesc")}</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {m.recordings.map(rec => (
                  <div key={rec.id} className="bg-card rounded-xl border border-border overflow-hidden group">
                    <div className="relative aspect-video bg-muted">
                      {rec.thumbnail ? (
                        <img src={rec.thumbnail} alt={rec.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-muted-foreground/40" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openPlayback(rec.playback)}
                          className="w-12 h-12 rounded-full bg-card/90 flex items-center justify-center hover:bg-card transition-colors shadow-lg"
                        >
                          <Play className="w-5 h-5 text-foreground ml-0.5" />
                        </button>
                      </div>
                      {rec.duration && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[11px] px-2 py-0.5 rounded font-mono">
                          {rec.duration}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-foreground truncate mb-0.5">{rec.name}</p>
                      <p className="text-xs text-muted-foreground mb-3">{rec.size}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs gap-1.5"
                          onClick={() => {
                            void downloadPlayback(rec.playback, `${rec.name}.webm`).catch(() =>
                              toast.error({ title: t("common.download"), description: "Could not open this recording." }),
                            )
                          }}
                        >
                          <Download className="w-3.5 h-3.5" />{t("common.download")}
                        </Button>
                        <Button size="sm" className="flex-1 text-xs gap-1.5" onClick={() => openPlayback(rec.playback)}>
                          <Play className="w-3.5 h-3.5" />{t("meetings.play")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Chat ── */}
        {activeTab === "chat" && (
          <div className="flex flex-col h-full">
            <div className="px-4 sm:px-6 py-3 border-b border-border shrink-0">
              <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2">
                <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                <input
                  value={chatSearch}
                  onChange={e => setChatSearch(e.target.value)}
                  placeholder={t("meetings.searchChatMessages")}
                  className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
              {filteredChat.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
                  <MessageCircle className="w-10 h-10 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">{t("meetings.noMessagesFound")}</p>
                </div>
              ) : (
                filteredChat.map(msg => (
                  <div key={msg.id} className="flex gap-3">
                    <Avatar name={msg.sender || t("panels.unknown")} size={32} />
                    <div className="flex-1 bg-card rounded-xl border border-border/50 px-3 py-2">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-foreground">{msg.sender || t("panels.unknown")}</span>
                        <span className="text-[10px] text-muted-foreground">{msg.time}</span>
                      </div>
                      {msg.attachment ? (
                        <AttachmentLink href={msg.attachment.url} meetingId={meetingUuid} className="text-sm text-primary underline break-all">
                          {msg.attachment.name}
                        </AttachmentLink>
                      ) : (
                        <p className="text-sm text-foreground">{msg.text}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Transcription ── */}
        {activeTab === "transcription" && (
          <div className="flex flex-col h-full">
            {/* Search + Download bar */}
            <div className="px-4 sm:px-6 py-3 border-b border-border shrink-0 flex items-center gap-2">
              <div className="flex items-center gap-2 bg-muted/50 rounded-xl px-3 py-2 flex-1">
                <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                <input
                  value={transcriptSearch}
                  onChange={e => setTranscriptSearch(e.target.value)}
                  placeholder={t("meetings.searchTranscript")}
                  className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
                />
              </div>
              {m.transcriptionAvailable && (
                <Button
                  variant="outline" size="sm"
                  className="gap-1.5 text-xs shrink-0"
                  onClick={() => toast.success({ title: t('meetings.transcriptDownloaded'), description: t('meetings.transcriptDownloadedDesc') })}
                >
                  <Download className="w-3.5 h-3.5" />{t("common.download")}
                </Button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
              {!m.transcriptionAvailable ? (
                /* ── No transcript empty state ── */
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center py-8">
                  <img src={noTranscriptSvg} alt="No transcript" className="w-36 h-36 opacity-80" />
                  <div>
                    <p className="text-base font-semibold text-foreground">{t("meetings.noTranscriptAvailable")}</p>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xs">
                      {t("meetings.transcriptsNotSaved")}
                    </p>
                  </div>
                </div>
              ) : filteredTranscript.length === 0 ? (
                /* ── Search empty state ── */
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                  <img src={noTranscriptSvg} alt="No results" className="w-24 h-24 opacity-60" />
                  <p className="text-sm text-muted-foreground">{t("meetings.searchTranscript")} &ldquo;{transcriptSearch}&rdquo;</p>
                </div>
              ) : (
                /* ── Transcript entries ── */
                <div className="space-y-3">
                  {filteredTranscript.map((entry, i) => (
                    <div key={i} className="flex gap-3 group">
                      <Avatar name={entry.speaker} size={32} />
                      <div className="flex-1 bg-card rounded-xl border border-border/50 px-3 py-2">
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="text-xs font-semibold text-foreground">{entry.speaker}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">{entry.time}</span>
                          <button
                            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
                            onClick={() => {
                              navigator.clipboard.writeText(entry.text)
                              toast.success({ title: t('meetings.transcriptCopied'), description: t('meetings.transcriptCopiedDesc') })
                            }}
                          >
                            <Copy className="w-3 h-3 text-muted-foreground" />
                          </button>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">{entry.text}</p>
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground/50 text-center pt-4 border-t border-border/30">
                    {t("meetings.autoGenerated")}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
